import { RootState } from "@/redux/store";
import CustomCollapsible from "@/components/ui/collapsibleCard/collapsible-card";
import { v4 as uuidv4 } from "uuid";
import { Input } from "@/components/ui/input";
import { useGetAllMemberQuery, } from "@/services/memberApi";
import React, { useMemo } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

import { useSelector } from "react-redux";
import { useGetStaffsQuery } from "@/services/staffsApi";
import { FormControl, FormItem } from "@/components/ui/form";
import { InvoiceDetails } from "@/types/invoice";
import { AutoComplete, Option } from "@/components/ui/autocomplete";
import Combobox from "@/components/ui/common/combobox";
import { useGetTaxGroupQuery } from "@/services/salesTaxApi";
import { TaxGroupType } from "@/types/enums";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface InvoiceHeaderProps {
  control: Control<InvoiceDetails>;
  errors: FieldErrors<InvoiceDetails>;
  watch: UseFormWatch<InvoiceDetails>;
  setValue: UseFormSetValue<InvoiceDetails>;
  action: string;
}
const InvoiceHeader = ({
  control,
  errors,
  watch,
  setValue,
  action,
}: InvoiceHeaderProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const watcher = watch()
  const { data: staffData } = useGetStaffsQuery({
    org_id: orgId,
    query: `sort_key=id&sort_order=desc&status=active`,
  });

  const staffOptions = useMemo(() => {
    return Array.isArray(staffData?.data) ? staffData?.data.map((staff) => ({
      value: String(staff.id),
      label: `${staff.own_staff_id} • ${staff.first_name} ${staff.last_name}`,
    })) : [];
  }, [staffData]);

  const { data: taxGroupsData } = useGetTaxGroupQuery({ query: "", org_id: orgId }, {
    skip: !orgId,
  });

  const memberTaxGroups = useMemo(() => {
    return Array.isArray(taxGroupsData?.data)
      ? taxGroupsData?.data.filter((tg: any) => tg.type === TaxGroupType.Member)
      : [];
  }, [taxGroupsData]);

  const {
    data: users,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetAllMemberQuery(
    { org_id: orgId, query: `` },
  );

  // Memoize member options with unique labels
  const memberOptions = useMemo(() => {
    if (!users?.data) return [];
    return users.data.map((data) => ({
      ...data,
      own_id: data.own_member_id,
      value: "" + data.id,
      // Make label unique by including own_member_id
      label: data.own_member_id
        ? `${data.own_member_id} • ${data.first_name} ${data.last_name}`
        : `${data.first_name} ${data.last_name}`,
    }));
  }, [users]);

  // Get the selected member option for all modes
  const selectedMemberOption = useMemo(() => {
    if (watcher.member_id && watcher.member_id > 0) {
      const member = memberOptions.find(
        (option) => option.value === watcher.member_id?.toString() || option.value === watcher.id?.toString()
      );
      return member ? {
        value: member.value,
        label: member.label,
      } as Option : undefined;
    }
    return undefined;
  }, [watcher.member_id, watcher.id, memberOptions]);

  const handleUserSelect = (selectedUser: any) => {
    setValue("member_id", selectedUser.id);
    setValue(
      "member_name",
      selectedUser.first_name + " " + selectedUser.last_name
    );
    setValue("member_email", selectedUser.email);
    setValue("member_address", selectedUser.address_1);
    setValue("member_nic", selectedUser.nic);
    setValue("member_gender", selectedUser.gender);
    setValue("is_business", selectedUser.is_business);
    console.log({selectedUser})

    const customerTaxGroup = Array.isArray(selectedUser.tax_group)
      ? selectedUser.tax_group[0]
      : selectedUser.tax_group;
    
    // Safely extract tax group ID
    const taxGroupId = typeof customerTaxGroup === 'object' ? customerTaxGroup?.id : customerTaxGroup;
    setValue("member_tax_group_id", taxGroupId || selectedUser.tax_group_id || 0);
  };

  return (
    <div className=" rounded-2xl bg-white">
      <CustomCollapsible
        key={uuidv4()}
        hideToggle={false}
        isOpenDefault={true}
        title="Invoice Detail"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Bill To */}
          <div>
            <label className="text-base font-normal block mb-1">
              Bill To <span className="text-red-500">*</span>
            </label>
            <Controller
              control={control}
              name="member_id"
              rules={{
                required: "Required",
              }}
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => (
                <>
                  <Combobox
                    list={memberOptions}
                    disabled={action == "edit" && Number(watcher.member_id) > 0}
                    defaultValue={
                      (value && value > 0 && memberOptions?.find(
                        (option) => option.value == String(value)
                      )?.label) || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      if (!selectedLabel) {
                        // Clear selection
                        onChange(0);
                        setValue("member_id", 0);
                        setValue("member_name", "");
                        setValue("member_email", "");
                        setValue("member_address", "");
                        setValue("member_nic", "");
                        setValue("member_gender", "");
                        setValue("is_business", false);
                        setValue("member_tax_group_id", 0);
                        return;
                      }
                      const selectedOption = memberOptions?.find(
                        (option) => option.label === selectedLabel
                      );
                      if (selectedOption) {
                        handleUserSelect(selectedOption);
                      }
                    }}
                    label="Member"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Invoice Number */}
          <div>
            <label className="text-base font-normal block mb-1">
              Invoice Number <span className="text-red-500">*</span>
            </label>
            <Controller
              name="invoice_number"
              control={control}
              defaultValue=""
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    id="invoice_number"
                    placeholder="Enter invoice number"
                    disabled
                  />
                  {fieldState.error && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldState.error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>

          {/* Processed By */}
          <div>


            <Controller
              name="seller_id"
              rules={{
                validate: (value) =>
                  (value !== undefined && value !== null && value !== 0) ||
                  "Required",
              }}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <label className="text-base font-normal block mb-1">
                    Processed By <span className="text-red-500">*</span>
                  </label>
                  <Combobox
                    list={staffOptions}
                    defaultValue={
                      (value && value > 0 && staffOptions?.find(
                        (option) => option.value == String(value)
                      )?.label) || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      if (!selectedLabel) {
                        // Clear selection
                        onChange(0);
                        setValue("seller_id", 0);
                        setValue("seller_name", "");
                        return;
                      }
                      const selectedOption = staffOptions?.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? Number(selectedOption.value)
                          : 0
                      );
                      // the label will have the own_staff_id - first_name last_name
                      const splitLabel = selectedOption?.label ? selectedOption.label.split(" • ") : [];
                      const name = splitLabel.length > 1 ? splitLabel[1] : "";
                      setValue("seller_name", name);
                    }}
                    label="Processed By"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />

          </div>

          {/* Invoice Date */}
          <div>
            <label className="text-base font-normal block mb-1">
              Invoice Date<span className="text-red-500">*</span>
            </label>
            <Controller
              name="payment_date"
              control={control}
              defaultValue=""
              disabled={true}
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={
                        field.value
                          ? new Date(field.value).toISOString().split("T")[0]
                          : ""
                      }
                      disabled={true}
                      className="w-full text-center font-normal"
                    />
                  </FormControl>
                  {fieldState.error && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldState.error.message}
                    </p>
                  )}
                </FormItem>
              )}
              rules={{ required: "Required" }}
            />
          </div>

          {/* Member Tax Group */}
          <div>
            <label className="text-base font-normal block mb-1">
              Member Tax Group
            </label>
            <Controller
              name="member_tax_group_id"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value?.toString() || "0"}
                  onValueChange={(value) => {
                    field.onChange(Number(value));
                  }}
                  disabled={action === "edit" || !watcher.member_id}
                >
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue placeholder="Select Member Tax Group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">None</SelectItem>
                    {memberTaxGroups.map((tg: any) => (
                      <SelectItem key={tg.id} value={tg.id?.toString() || ""}>
                        {tg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>
      </CustomCollapsible>
    </div>
  );
};

export default InvoiceHeader;
